import { faMagnifyingGlass, faPlus } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Group, SimpleGrid, Stack, useMantineTheme } from '@mantine/core';
import type { JSX } from 'react';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRouteMatch } from 'react-router-dom';

import {
  Breadcrumbs,
  Button,
  CardV2,
  Heading,
  Input,
  PageHeader,
  Pill,
  Text,
  Toolbar,
} from '@feathr/components';

import PrototypeDoneModal from '../components/PrototypeDoneModal';
import type { ISampleProjectDetail } from '../sampleData';
import { sampleProjectDetails } from '../sampleData';
import { buildPrototypeBreadcrumbs } from '../utils/buildBreadcrumbs';
import { formatDateRange } from '../utils/formatDate';
import { statusLabel, statusTheme } from '../utils/statusPalette';

import * as shellStyles from '../Shell/PrototypeShell.css';

function ProjectsPage(): JSX.Element {
  const { t } = useTranslation();
  const theme = useMantineTheme();
  const match = useRouteMatch<{ accountId: string }>('/:accountId/ia-prototype');
  const base = match?.url ?? '';
  const [search, setSearch] = useState<string>('');
  const [isDoneOpen, setDoneOpen] = useState<boolean>(false);

  const breadcrumbItems = buildPrototypeBreadcrumbs(
    base,
    { label: t('Marketing'), defaultPath: '/marketing/all' },
    t('Projects'),
    '/marketing/projects',
  );

  const filtered = useMemo<ISampleProjectDetail[]>(() => {
    const lower = search.trim().toLowerCase();
    if (!lower) {
      return sampleProjectDetails;
    }
    return sampleProjectDetails.filter((p) => p.name.toLowerCase().includes(lower));
  }, [search]);

  const actions = (
    <Toolbar>
      <Button
        onClick={(): void => setDoneOpen(true)}
        prefix={<FontAwesomeIcon icon={faPlus} />}
        type={'primary'}
      >
        {t('New project')}
      </Button>
    </Toolbar>
  );

  return (
    <>
      <PageHeader
        actions={actions}
        BreadcrumbsComponent={<Breadcrumbs breadcrumbItems={breadcrumbItems} />}
        title={t('Projects')}
      />
      <Stack className={shellStyles.body}>
        <Group justify={'space-between'}>
          <Box w={320}>
            <Input
              isClearable={true}
              onChange={(next?: string): void => setSearch(next ?? '')}
              placeholder={t('Search by name…')}
              prefix={<FontAwesomeIcon icon={faMagnifyingGlass} />}
              type={'text'}
              value={search}
            />
          </Box>
        </Group>
        <SimpleGrid cols={3} spacing={theme.spacing['4']}>
          {filtered.map((p) => (
            <CardV2 key={p.id} onClick={(): void => setDoneOpen(true)} width={'full'}>
              <CardV2.Content title={p.name}>
                <Stack gap={theme.spacing['2']}>
                  <Text variant={'smallRegular'}>{p.description}</Text>
                  <Group gap={theme.spacing['2']}>
                    <Pill theme={statusTheme(p.status)}>{statusLabel(p.status)}</Pill>
                    <Pill theme={'neutral'}>
                      {t('{{count}} campaigns', { count: p.campaignCount })}
                    </Pill>
                    {p.raised !== null && <Pill theme={'green'}>${p.raised.toLocaleString()}</Pill>}
                  </Group>
                  <Text variant={'smallRegular'}>{formatDateRange(p.startDate, p.endDate)}</Text>
                </Stack>
              </CardV2.Content>
            </CardV2>
          ))}
        </SimpleGrid>
        {filtered.length === 0 && (
          <Box>
            <Heading order={4} variant={'h4'}>
              {t('No projects match your search')}
            </Heading>
          </Box>
        )}
      </Stack>

      <PrototypeDoneModal
        description={t('The project workflow opens at this point. The prototype ends here.')}
        onClose={(): void => setDoneOpen(false)}
        opened={isDoneOpen}
        title={t('New project')}
      />
    </>
  );
}

export default ProjectsPage;
